import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import RouteIcon from '@mui/icons-material/Route';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { routesApi, fromAxiosError } from '@/core/api';
import type { RouteListItem } from '@/core/api';
import { BackToTopFab, EmptyState, HelpAlert, StatusChip, usePageTitle, useFormattedDate } from '@/shared';
import { relativeTime } from '@/core/utils';

/**
 * A driver's landing page.
 *
 * This used to be a single centred card reading "Your active job will appear
 * here once the tow-requests feature is enabled" — which was wrong twice over:
 * the feature is enabled, and the page had no way to notice. A driver signing in
 * on the desk saw a dead end while their nine assigned routes sat one click away
 * behind a Routings page that was itself erroring.
 *
 * So it shows the work. Active routes first, because a driver mid-haul opening
 * this on a laptop is looking for the one they are on; then what is planned
 * next. The mobile-app pointer stays, demoted to a footnote — it is true that
 * field navigation belongs on the phone, but it is not an excuse for the web to
 * show nothing.
 */

/** Trip statuses that mean "this is the job in hand". */
const ACTIVE_STATUSES = new Set(['ACTIVE', 'INPROCESS', 'TOARRIVE']);
/** Terminal states — kept out of both lists, counted in the summary. */
const DONE_STATUSES = new Set(['COMPLETED', 'CANCELLED', 'CLOSE']);

export default function DriverHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatLocalized = useFormattedDate();
  usePageTitle('home.driver.pageTitle');

  const [routes, setRoutes] = useState<RouteListItem[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchRoutes = useCallback(async (signal: AbortSignal) => {
    setErrorMessage(null);
    try {
      // The driver-scoped action, not `GetList` — a driver's token carries no
      // "routings" menu permission and `GetList` answers Unauthorized for it.
      const result = await routesApi.getDriverRoutesList({ page: 0, pageSize: 50 });
      if (!signal.aborted) setRoutes(result.elements);
    } catch (err) {
      if (!signal.aborted) {
        setErrorMessage(fromAxiosError(err).message);
        setRoutes([]);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetchRoutes(controller.signal);
    return () => controller.abort();
  }, [fetchRoutes]);

  const loading = routes === null;
  const active = (routes ?? []).filter((r) => ACTIVE_STATUSES.has(r.tripStatus?.toUpperCase()));
  const planned = (routes ?? []).filter(
    (r) =>
      !ACTIVE_STATUSES.has(r.tripStatus?.toUpperCase()) &&
      !DONE_STATUSES.has(r.tripStatus?.toUpperCase()),
  );
  const doneCount = (routes ?? []).filter((r) =>
    DONE_STATUSES.has(r.tripStatus?.toUpperCase()),
  ).length;

  const renderRoute = (route: RouteListItem) => (
    <Card key={route.routeId} variant="outlined">
      <CardActionArea
        onClick={() => navigate(`/routings/${String(route.routeId)}`)}
        sx={{ p: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <RouteIcon color="action" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {route.description || t('home.driver.untitledRoute', { 0: route.routeId })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {relativeTime(route.creationDate) ?? formatLocalized(route.creationDate)}
              {route.customer ? ` · ${route.customer}` : ''}
            </Typography>
          </Box>
          <StatusChip
            status={route.tripStatus}
            label={t(`ROUTINGS.OPTIONS.status.${route.tripStatus}`, {
              defaultValue: route.tripStatus,
            })}
          />
        </Stack>
      </CardActionArea>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <HelpAlert
        persistKey="home.driver.welcome"
        severity="info"
        variant="outlined"
        title={t('HELP.home.driver.welcome.title')}
      >
        {t('HELP.home.driver.welcome.body')}
      </HelpAlert>

      {errorMessage !== null ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}

      {loading ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
        </Stack>
      ) : (routes ?? []).length === 0 ? (
        <EmptyState
          icon={<RouteIcon />}
          title={t('home.driver.emptyTitle')}
          description={t('home.driver.emptyBody')}
        />
      ) : (
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6">{t('home.driver.onTheRoad')}</Typography>
              <Chip size="small" label={active.length} />
            </Stack>
            {active.length > 0 ? (
              <Stack spacing={1}>{active.map(renderRoute)}</Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('home.driver.noneActive')}
              </Typography>
            )}
          </Box>

          {planned.length > 0 ? (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6">{t('home.driver.comingUp')}</Typography>
                <Chip size="small" label={planned.length} />
              </Stack>
              <Stack spacing={1}>{planned.map(renderRoute)}</Stack>
            </Box>
          ) : null}

          <Box>
            <Button component={RouterLink} to="/routings" variant="outlined">
              {t('home.driver.allRoutesCta', { 0: (routes ?? []).length })}
            </Button>
            {doneCount > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                {t('home.driver.finishedCount', { 0: doneCount })}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      )}

      {/* Still true, just no longer the whole page. */}
      <Divider sx={{ my: 3 }} />
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <PhoneAndroidIcon fontSize="small" color="success" />
        <Typography variant="body2" color="text.secondary">
          {t('home.driver.message')}
        </Typography>
      </Stack>
      <BackToTopFab />
    </Box>
  );
}
